/**
 * The shared map as a corner minimap over the story (Maz, 2026-10-20: the table is story-first, the
 * transcript gets the room).
 *
 * Shown small — the real map, scaled down, not a second picture — and a click opens it full size in a dialog.
 * It opens by itself while a `move` is being declared, because picking the destination is a click ON the map
 * and a thumbnail cannot take one; it closes again once the point is picked. Theatre of the Mind has no map,
 * so there is nothing to show and nothing renders.
 */

import { useEffect, useState } from 'react';

import { MapPanel, type MapPanelProps } from './MapPanel.js';

/** The width the map is laid out at before it is shrunk to the thumbnail. */
const FULL_WIDTH_REM = 40;
const THUMB_SCALE = 0.3;

export function MiniMap({
  forceOpen,
  ...props
}: MapPanelProps & {
  /** True while the map has to be big enough to click (choosing a destination). */
  readonly forceOpen?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const shown = open || forceOpen === true;

  useEffect(() => {
    if (!shown) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shown]);

  if (props.mode === 'theatre') return null;

  return (
    <>
      <button
        type="button"
        aria-label="Open the map"
        title="Open the map"
        onClick={() => setOpen(true)}
        className="block h-24 w-36 overflow-hidden rounded-md border border-neutral-300 bg-white text-left shadow-sm hover:border-neutral-500 dark:border-neutral-700 dark:bg-neutral-900"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none block origin-top-left"
          style={{ width: `${FULL_WIDTH_REM}rem`, transform: `scale(${THUMB_SCALE})` }}
        >
          <MapPanel {...props} />
        </span>
      </button>
      {shown && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Map"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4"
          onClick={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-4xl rounded-md bg-white p-3 shadow-lg dark:bg-neutral-950">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold">Map</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
              >
                Close
              </button>
            </div>
            {/* Closed on travel, so the story the journey writes is what is on screen. */}
            <MapPanel
              {...props}
              {...(props.onTravel ? { onTravel: (placeId: string) => { setOpen(false); props.onTravel!(placeId); } } : {})}
            />
          </div>
        </div>
      )}
    </>
  );
}
